import {
  AdFoxBanner,
  calculatedParamsMiddleware,
  fetchTargets,
  IAdFoxBannerContext,
  IAdFoxParams,
  mergeMiddlewares,
} from '@cian/adfox';
import { IAdFoxBannerOnLoadData, IAdFoxBannerOnErrorError } from '@cian/adfox-component';
import * as React from 'react';
import { useApplicationContext } from '../../utils/applicationContext';
import { NewAdfoxBanner } from '../NewAdfoxBanner';
import { IAdfoxConstants } from './constants';
import { ErrorBoundary } from '../ErrorBoundary';
// eslint-disable-next-line import/no-restricted-paths
import { useAdsDisabled } from '../../../browser/utils/hooks/useAdsDisabled';
import { LazyLoad } from '../LazyLoad';

const SECTION_TYPES: { [section: string]: number } = {
  index: 1,
  sale: 2,
  rent: 3,
  'daily-rent': 4,
  commercial: 5,
  'new-buildings': 6,
};

export const adfoxMiddleware = (constants: IAdfoxConstants, section: string = 'index', onError?: () => void) => {
  return mergeMiddlewares(
    calculatedParamsMiddleware(),
    fetchTargets('apps', {
      baseUrl: `//${window.__mainpage_api_host__ || window.location.host}/`,
      section_type: SECTION_TYPES[section] || 1,
    }).catch(() => {
      if (onError) {
        onError();
      }
    }),
    Promise.resolve(constants),
  );
};

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  banner(context: IAdFoxBannerContext): React.ReactElement<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  failureBanner?(error: Error): React.ReactElement<any>;
  constants: IAdfoxConstants;
  section?: string;
  key?: string;
}

export interface IAdfoxNewProps {
  constants: IAdfoxConstants;
  section?: string;
  key?: string;
  onLoad?(data: IAdFoxBannerOnLoadData): void;
  onStub?(): void;
  onError?(error: IAdFoxBannerOnErrorError): void;
  replaceRenderEmpty?: React.ReactElement;
  withPostView?: boolean;
  label?: string;
}

export const Adfox = (props: IProps) => {
  // компонент AdFoxBanner может вернуть null,
  // что пораждает ошибку типизации. Попытка поднять версии реакта и тайпскрипта
  // до версии в либе @cian/adfox не увенчалась успехом, и ошибка продолжала
  // воспроизводиться. Таким образом, вот такой хак.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AnyComponent = AdFoxBanner as any;

  return (
    <AnyComponent
      middleware={() => adfoxMiddleware(props.constants, props.section)}
      renderFetched={props.banner}
      renderFailure={props.failureBanner}
    ></AnyComponent>
  );
};

const AdfoxNewComponent = (props: IAdfoxNewProps) => {
  const { logger } = useApplicationContext();
  const { onStub, replaceRenderEmpty } = props;

  const onError = React.useCallback(
    (error: Error) => {
      logger.error(error, {
        message: 'Failed to render adfox banner',
        domain: 'AdfoxNew',
      });

      if (onStub) {
        onStub();
      }
    },
    [onStub, logger],
  );

  const onRenderEmpty = () => {
    if (replaceRenderEmpty) {
      return replaceRenderEmpty;
    }

    return <div />;
  };

  return (
    <LazyLoad
      key={props.key}
      offset={300}
      onLoad={() => adfoxMiddleware(props.constants, props.section, onStub)}
      renderLoaded={(result: IAdFoxParams) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pr, pr1, ...rest } = result;

        return (
          <ErrorBoundary onError={onError}>
            <NewAdfoxBanner
              withPostView={props.withPostView}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              params={rest as any}
              onLoad={props.onLoad}
              onStub={props.onStub}
              onError={props.onError}
              label={props.label}
              section={props.section}
              width="100%"
              minHeight="80px"
              lazyLoad
            />
          </ErrorBoundary>
        );
      }}
      renderEmpty={onRenderEmpty}
    />
  );
};

export const AdfoxNew = (props: IAdfoxNewProps) => {
  const isAdsDisabled = useAdsDisabled(() => {
    if (props.onStub) {
      props.onStub();
    }
  }, [props.onStub]);

  if (isAdsDisabled) {
    return null;
  }

  return <AdfoxNewComponent {...props} />;
};
